import { Col, Table, TableActionBarRow, Text, ValueOrFallback } from "melco-ui";
import { useTranslation } from "react-i18next";
import {
  FormattedDate,
  MelcoModelsUserSubscriptionListItem,
  Search,
  TableCellLink,
} from "melco-shared-logic";
import { useLicenses } from "../../hooks/infrastructure/useLicenses";
import { StatusColorMap } from "../../types/status";
import dayjs from "dayjs";
import { formatUTCDate } from "../../helper/date/dateHelper";
import { AsyncTooltip, TooltipBulletItem, TooltipContainer, TooltipText, TooltipTitle } from "../tootltip/AsyncTooltip";
import { useState } from "react";
import { useSubscriptionGetPendingChanges } from "../../hooks/infrastructure/useSubscriptionGetPendingChanges";
import { capitalize } from "../../helper/common/util";
import styled from "styled-components";

const DETAIL_VIEW_PATH = "/infrastructure/licenses";
export const LicenseTypeCell = ({ licenseType }: { licenseType: number }) => {
  const { t } = useTranslation();
  const label = t(`infrastructureLicenses.licenseTypes.${licenseType}`, '');
  return <>{label}</>;
};

const CellWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const TooltipWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
`;

export const LicensesList: React.FC = () => {

  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
  } = useLicenses();

  //const navigate = useNavigate();

  const { t } = useTranslation();

  // const { onRow } = useTableSelection<MelcoModelsUserSubscriptionListItem>(
  //   (row) => {
  //     navigate([DETAIL_VIEW_PATH, row.id].join("/"));
  //   }
  // );

  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);


  const handleToggle = (id: string | null) => {
    if (activeTooltipId === id) {
      // Clicked the same icon → close it
      setActiveTooltipId(null);
    } else {
      // Clicked another icon  open the new one

      setTimeout(() => {
        setActiveTooltipId(id);
      }, 500);
    }
  };

  const displayExpiry = (item: MelcoModelsUserSubscriptionListItem) => {
    const now = dayjs();
    if (item.is_subscription === false) {
      return t("infrastructureLicenses.does_not_expire");
    }

    if (item.is_subscription === true) {
      if (!item.cancelled_on) {
        return t("infrastructureLicenses.does_not_expire");
      } else {
        if (dayjs(item.cancelled_on).isBefore(now)) {
          return t("infrastructureLicenses.expired");
        } else {
          if (item.cancelled_on) {
            return formatUTCDate(item.cancelled_on, "L");
          }
        }
      }
    }


    /**
     * It is fallback so that if anyhow is_subscription is not
     * coming in response , just to avoid run time exception / error
     * we will display Does not expire 
     */
    return t("machineLicenses.does_not_expire");
  };

  const columns = [
    {
      title: t("infrastructureLicenses.list.columns.display_name"),
      key: "display_name",
      dataIndex: "display_name",
      sorter: true,
      render: (_: string, item: MelcoModelsUserSubscriptionListItem) => (
        <ValueOrFallback
          value={item.display_name}
          render={(value) => (
            <TableCellLink to={[DETAIL_VIEW_PATH, item.id].join("/")}>
              {value}
            </TableCellLink>
          )}
          fallback={t("global.fallback.empty")}
        />
      ),
      width: 150
    },
    {
      title: t("infrastructureLicenses.list.columns.product_name"),
      key: "product_name",
      dataIndex: "product_name",
      sorter: true,
      width: 90
    },
    {
      title: t("infrastructureLicenses.list.columns.product_version_number"),
      key: "product_version_number",
      dataIndex: "product_version_number",
      sorter: true,
      width: 80
    },
    // {
    //   title: t("infrastructureLicenses.list.columns.license_type"),
    //   key: "license_type",
    //   //dataIndex: "license_type",
    //   sorter: true,
    //   render: (_: string, item: MelcoModelsUserSubscriptionListItem) => (<>
    //     <LicenseTypeCell licenseType={item.license_type ?? -1} />
    //   </>)
    //   , width: 90
    // },
    {
      title: t("infrastructureLicenses.list.columns.product_level_name"),

      key: "product_level_name",
      dataIndex: "product_level_name",
      sorter: true,
      width: 90
    },
    {
      title: t("infrastructureLicenses.list.columns.assigned_user_name"),
      key: "assigned_user_name",
      dataIndex: "assigned_user_name",
      sorter: true,
      width: 110
    },
    {
      title: t("infrastructureLicenses.list.columns.activated_on"),
      key: "activated_on",
      dataIndex: "activated_on",
      sorter: true,
      width: 110,
      render: (_: string, item: MelcoModelsUserSubscriptionListItem) => (
        <FormattedDate date={item.activated_on} />
      ),
    },
    {
      title: t("infrastructureLicenses.list.columns.updated_date"),
      key: "updated_date",
      dataIndex: "updated_date",
      sorter: true,
      width: 110,
      render: (_: string, item: MelcoModelsUserSubscriptionListItem) => (
        <FormattedDate date={item.updated_date} />
      ),
    },
    {
      title: t("infrastructureLicenses.list.columns.renewal_date"),
      key: "renewal_date",
      dataIndex: "renewal_date",
      sorter: true,
      width: 110,
      render: (_: string, item: MelcoModelsUserSubscriptionListItem) => (
        <> {
          item.is_subscription
            ? (
              item.cancelled_on ? "" : (item.renewal_date
                ? formatUTCDate(item.renewal_date, "L")
                : "")
            )
            : t("infrastructureLicenses.no_renewal")
        }
        </>
      ),
    },
    {
      title: t("infrastructureLicenses.list.columns.expires_on"),
      key: "expires_on",
      dataIndex: "expires_on",
      sorter: true,
      width: 110,
      render: (_: string, item: MelcoModelsUserSubscriptionListItem) => (
        displayExpiry(item)
      ),
    },
    {
      title: t("infrastructureLicenses.list.columns.status"),
      key: "status",
      //dataIndex: "status",
      sorter: true,
      width: 110,
      render: (_: string, item: MelcoModelsUserSubscriptionListItem) => (
        <LicenseStatusCell item={item} />
      ),

    }];

  const LicenseStatusCell = ({ item }: { item: MelcoModelsUserSubscriptionListItem }) => {
    const { t } = useTranslation();
    const status = item?.status ?? -1;
    const color = StatusColorMap[status];
    const label = t(`infrastructureLicenses.licenseStatus.${status}`, '');

    return <CellWrapper><Text type={color}>{label}</Text>

      {
        status == 2 && (<TooltipWrapper><AsyncTooltip
          id={item.id ?? ""}
          key={item.id ?? ""}
          param={item.id ?? ""}
          isActive={activeTooltipId === item.id}
          onToggle={handleToggle}
          useDataHook={useSubscriptionGetPendingChanges}
          renderContent={(data) => {
            if (!data) return <TooltipText>{t("infrastructureLicenses.tooltip.no_data_available")}</TooltipText>;

            const date = formatUTCDate(data.sub_start_date, "YYYY-MM-DD");
            const from = capitalize(data?.change_from ?? "");
            const to = capitalize(data?.change_to ?? "");

            return (
              <TooltipContainer>
                <TooltipTitle>{t("infrastructureLicenses.tooltip.changes_apply", { date })}</TooltipTitle>
                <TooltipBulletItem>
                  {t("infrastructureLicenses.tooltip.billing_change", { from, to })}
                </TooltipBulletItem>
              </TooltipContainer>
            );
          }}
        />
        </TooltipWrapper>)

      }</CellWrapper>;
  };
  return (
    <>
      <TableActionBarRow>
        <Col xs={24} xl={17}>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        sticky
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="id"
        //        onRow={onRow}
        noDataMessage={t("infrastructureLicenses.list.empty")}
        errorComponent={errorComponent}
      />
    </>
  );
};
